import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import NewLayout from '../../WebLayout/NewLayout/NewLayout';
import { ClubNewDetailScreenProps } from './types';
import {
  Image,
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { STYLE_VARS } from '../../constants/style-vars';
import { Button } from '../../components/Button';
import { IS_ANDROID, IS_WEB } from '../../constants/platform';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import { newspaper_icon, share_icon } from '../../assets/base64';
import PageTitle from '../../components/PageTitle/PageTitle';
import { Footer } from '../../components/Footer';
import { COLORS, FONTS } from '../../constants';
import { INew } from '../../types';
import { NewApi } from '../../services/api';
import RenderHTML from 'react-native-render-html';
import dayjs from 'dayjs';
import { Loading } from '../../components';
import Pdf from 'react-native-pdf';
const ClubNewDetail: FC<ClubNewDetailScreenProps> = ({ navigation, route }) => {
  const id = route.params.id;
  const isDesktop = useIsDesktop();
  const { width } = useWindowDimensions();

  const [news, setNews] = useState<INew>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const setIsScrollingTrue = () => setIsScrolling(true);
  const setIsScrollingFalse = () => setIsScrolling(false);

  const isScrollingEnabled = useMemo(() => {
    return IS_ANDROID ? !isScrolling : true;
  }, [isScrolling]);

  const getNews = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await NewApi.getNewsDetail(id);
      setNews(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadPDF = useCallback(() => {
    if (news) {
      const anchorElement = document.createElement('a');
      anchorElement.href = news.document.url;
      anchorElement.download = news.document.filename;
      anchorElement.target = '_blank';
      anchorElement.click();
    }
  }, [news]);

  useEffect(() => {
    getNews(id);
  }, [id, getNews]);

  if (isDesktop()) {
    return (
      <NewLayout
        title="Club News"
        icon={
          IS_WEB
            ? addBase64Prefix(newspaper_icon)
            : require('../../assets/newspaper-icon.png')
        }
        haveGoBackButton={true}
      >
        {isLoading === false && news ? (
          <View
            style={[
              styles.borderDarkGrey,
              {
                paddingHorizontal: 20,
                paddingVertical: 30,
              },
            ]}
          >
            <Text style={[styles.titleText, { fontSize: 24 }]}>
              {news?.title}
            </Text>
            <View style={styles.titleSpan} />
            <Text style={styles.dateText}>
              {dayjs(new Date(news.date * 1000)).format('DD MMMM, YYYY')}
            </Text>
            <Text style={styles.contentText}>
              <RenderHTML
                contentWidth={width / 2.2}
                source={{ html: news.content }}
              />
            </Text>
            {news.thumb && (
              <Image
                source={{ uri: news?.thumb }}
                style={{
                  width: '100%',
                  height: 540,
                  resizeMode: 'cover',
                  marginBottom: 30,
                }}
              />
            )}
            {news.document && (
              <>
                <View
                  style={{
                    marginBottom: 20,
                  }}
                >
                  <iframe
                    src={`${news.document.url}#view=FitH&toolbar=0&background=0&statusbar=0&messages=0&navpanes=0`}
                    style={{
                      height: '120vh',
                    }}
                    frameBorder={0}
                    typeof={'application/pdf'}
                  ></iframe>
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    style={STYLE_VARS.blueButton}
                    text="DOWNLOAD PDF"
                    onPress={() => {
                      Linking.openURL(news.document.url);
                    }}
                  />
                </View>
              </>
            )}
          </View>
        ) : isLoading === true ? (
          <View style={{ marginBottom: 20 }}>
            <Loading />
          </View>
        ) : (
          <></>
        )}
      </NewLayout>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={true}
      scrollEnabled={isScrollingEnabled}
      nestedScrollEnabled={true}
    >
      <View style={styles.container}>
        <PageTitle
          title="Club News"
          icon={require('../../assets/newspaper-icon.png')}
          haveGoBackButton={true}
        />
      </View>
      {isLoading === false && news ? (
        <View style={styles.container}>
          <View
            style={[
              styles.borderDarkGrey,
              {
                paddingHorizontal: 16,
                paddingVertical: 18,
                marginBottom: 20,
              },
            ]}
          >
            <Text style={[styles.titleText, { fontSize: 16 }]}>
              {news?.title}
            </Text>
            <View style={styles.titleSpan} />
            <Text style={styles.dateText}>
              {dayjs(new Date(news.date * 1000)).format('DD MMMM, YYYY')}
            </Text>
            <View style={{ width: width - 80, marginBottom: 10 }}>
              <RenderHTML
                contentWidth={width - 80}
                source={{ html: news.content }}
                tagsStyles={{
                  body: {
                    color: COLORS.DARKER_GREY,
                    fontFamily: FONTS.AvenirMedium,
                  },
                  p: {
                    color: COLORS.DARKER_GREY,
                    fontFamily: FONTS.AvenirMedium,
                  },
                }}
              />
            </View>
            {news.thumb && (
              <Image
                source={{ uri: news?.thumb }}
                style={{
                  width: '100%',
                  height: 190,
                  resizeMode: 'cover',
                  marginBottom: 10,
                }}
              />
            )}
            {news.document && (
              <>
                <View
                  style={{
                    marginBottom: 10,
                  }}
                >
                  {IS_WEB ? (
                    <iframe
                      src={`${news.document.url}#view=FitH&toolbar=0&background=0&statusbar=0&messages=0&navpanes=0`}
                      style={{
                        height: 410,
                      }}
                      frameBorder={0}
                      typeof={'application/pdf'}
                    ></iframe>
                  ) : (
                    <View
                      onTouchStart={setIsScrollingTrue}
                      onTouchEnd={setIsScrollingFalse}
                      onTouchCancel={setIsScrollingFalse}
                    >
                      {/* Disable on website */}
                      {/* <Pdf
                        source={{ uri: news.document.url, cache: true }}
                        style={{
                          width: '100%',
                          height: 410,
                        }}
                        trustAllCerts={false}
                        // horizontal={IS_ANDROID ? true : false}
                        showsHorizontalScrollIndicator
                      /> */}
                    </View>
                  )}
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    style={STYLE_VARS.blueButton}
                    text="DOWNLOAD PDF"
                    onPress={() => {
                      Linking.openURL(news.document.url);
                    }}
                  />
                </View>
              </>
            )}
          </View>
        </View>
      ) : isLoading === true ? (
        <View style={{ marginBottom: 20 }}>
          <Loading />
        </View>
      ) : (
        <></>
      )}
      <Footer />
    </ScrollView>
  );
};

export default ClubNewDetail;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  borderDarkGrey: {
    borderColor: STYLE_VARS.fccDarkGrey,
    borderWidth: 0.5,
  },
  titleText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 10,
  },
  titleSpan: {
    width: 30,
    height: 3,
    backgroundColor: STYLE_VARS.primaryColor,
    marginBottom: 15,
  },
  dateText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 15,
    color: STYLE_VARS.fccDarkerGrey,
    marginBottom: 15,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  contentText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 15,
    color: STYLE_VARS.fccDarkerGrey,
    marginBottom: 25,
    textTransform: 'uppercase',
  },
});
